"use client";

import * as React from "react";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, CheckCircle2, ChevronsUpDown, Loader2 } from "lucide-react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { updateVenue, checkSlug } from "@/services/venue";
import { useOrganisations } from "@/hooks/use-organisations";
import type { Venue, UpdateVenueDto } from "@/types";
import { cn, isPhoneValid, getPhoneNationalNumber } from "@/lib/utils";

interface EditVenueDialogProps {
  venue: Venue;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function EditVenueDialog({
  venue,
  open,
  onOpenChange,
  onSuccess,
}: EditVenueDialogProps) {
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedOrganisationId, setSelectedOrganisationId] = React.useState<
    string | null
  >(venue.organisation?.id || null);
  const [name, setName] = React.useState(venue.name);
  const [slug, setSlug] = React.useState(venue.slug || "");
  const [slugStatus, setSlugStatus] = React.useState<
    "idle" | "checking" | "available" | "taken" | "invalid"
  >("idle");
  const [slugRequired, setSlugRequired] = React.useState(false);
  const [phoneNumber, setPhoneNumber] = React.useState(venue.phoneNumber || "");
  const [countryCode, setCountryCode] = React.useState("us");
  const [email, setEmail] = React.useState(venue.email || "");
  const [address, setAddress] = React.useState({
    addressLine1: venue.address?.addressLine1 || "",
    addressLine2: venue.address?.addressLine2 || "",
    city: venue.address?.city || "",
    state: venue.address?.state || "",
    zipcode: venue.address?.zipcode || "",
    country: venue.address?.country || "",
  });

  const queryClient = useQueryClient();

  // Fetch all organisations
  const { data: organisationsData, isLoading: isLoadingOrganisations } =
    useOrganisations({
      pageNumber: 1,
      pageSize: 1000,
      search: searchQuery || undefined,
    });

  // Reset form data each time the dialog opens
  React.useEffect(() => {
    if (!open) return;
    setName(venue.name);
    setSlug(venue.slug || "");
    setSlugStatus("idle");
    setSlugRequired(false);
    setPhoneNumber(venue.phoneNumber || "");
    setEmail(venue.email || "");
    setSelectedOrganisationId(venue.organisation?.id || null);
    setAddress({
      addressLine1: venue.address?.addressLine1 || "",
      addressLine2: venue.address?.addressLine2 || "",
      city: venue.address?.city || "",
      state: venue.address?.state || "",
      zipcode: venue.address?.zipcode || "",
      country: venue.address?.country || "",
    });
  }, [open, venue]);

  // Debounced slug availability check
  React.useEffect(() => {
    const trimmed = slug.trim();

    if (!trimmed || trimmed === (venue.slug || "")) {
      setSlugStatus("idle");
      return;
    }

    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(trimmed)) {
      setSlugStatus("invalid");
      return;
    }

    setSlugStatus("checking");
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { exists } = await checkSlug(trimmed);
        if (cancelled) return;
        setSlugStatus(exists ? "taken" : "available");
      } catch (err) {
        if (cancelled) return;
        console.error("Error checking slug:", err);
        setSlugStatus("idle");
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [slug, venue.slug]);

  // Mutation for updating a venue
  const mutation = useMutation({
    mutationFn: (data: UpdateVenueDto) => updateVenue(venue.id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["venues"] });
      toast.success(`Venue "${data.name}" updated successfully!`);
      onOpenChange(false);
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.message || "Failed to update venue";
      toast.error(errorMessage);
      console.error("Error updating venue:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Venue name is required");
      return;
    }

    if (!selectedOrganisationId) {
      toast.error("Organisation is required");
      return;
    }

    const trimmedSlug = slug.trim();
    if (!trimmedSlug) {
      setSlugRequired(true);
      return;
    }
    if (trimmedSlug !== (venue.slug || "")) {
      if (slugStatus === "invalid") {
        toast.error(
          "Slug must be lowercase letters, numbers, and hyphens only"
        );
        return;
      }
      if (slugStatus === "taken") {
        toast.error("Slug is already taken");
        return;
      }
      if (slugStatus === "checking") {
        toast.error("Please wait while we check slug availability");
        return;
      }
    }

    // Validate address fields - all are required
    if (!address.addressLine1.trim()) {
      toast.error("Address Line 1 is required");
      return;
    }
    if (!address.city.trim()) {
      toast.error("City is required");
      return;
    }
    if (!address.state.trim()) {
      toast.error("State is required");
      return;
    }
    if (!address.zipcode.trim()) {
      toast.error("Zipcode is required");
      return;
    }
    if (!address.country.trim()) {
      toast.error("Country is required");
      return;
    }
    // With forceDialCode the input always retains the dial code (e.g. "+1"),
    // so treat "dial code only" as an empty phone number.
    const hasPhoneNumber = !!getPhoneNationalNumber(phoneNumber, countryCode);
    if (hasPhoneNumber) {
      const isValid = isPhoneValid(phoneNumber.trim(), countryCode);
      if (!isValid) {
        toast.error("Please enter a valid phone number");
        return;
      }
    }

    const payload: UpdateVenueDto = {
      name: name.trim(),
      slug: trimmedSlug,
      phoneNumber: hasPhoneNumber ? phoneNumber.trim() : null,
      email: email.trim() || null,
      organisationId: selectedOrganisationId,
      address: {
        addressLine1: address.addressLine1.trim(),
        addressLine2: address.addressLine2.trim(),
        city: address.city.trim(),
        state: address.state.trim(),
        zipcode: address.zipcode.trim(),
        country: address.country.trim(),
      },
    };

    mutation.mutate(payload);
  };

  const selectedOrganisation = organisationsData?.data?.find(
    (org) => org.id === selectedOrganisationId
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] !flex !flex-col max-h-[85vh] p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>Edit Venue</DialogTitle>
          <DialogDescription>Update the venue details below.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 min-h-0"
        >
          <div className="grid gap-4 py-4 px-6 overflow-y-auto flex-1 min-h-0">
            {/* Venue Name */}
            <div className="grid gap-2">
              <Label htmlFor="name">
                Venue Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g., Grand Ballroom"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            {/* Slug */}
            <div className="grid gap-2">
              <Label htmlFor="slug">
                Slug <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="slug"
                  placeholder="e.g., grand-ballroom"
                  value={slug}
                  onChange={(e) => {
                    setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-"));
                    setSlugRequired(false);
                  }}
                  className={cn(
                    "pr-9",
                    slugRequired || slugStatus === "taken" || slugStatus === "invalid"
                      ? "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30"
                      : slugStatus === "available"
                      ? "border-green-500 focus-visible:border-green-500 focus-visible:ring-green-500/30"
                      : ""
                  )}
                />
                {slugStatus === "checking" && (
                  <Loader2 className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
                {slugStatus === "available" && (
                  <CheckCircle2 className="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-green-600" />
                )}
              </div>
              {slugRequired && (
                <p className="text-xs text-destructive">Slug is required.</p>
              )}
              {slugStatus === "invalid" && (
                <p className="text-xs text-destructive">
                  Use lowercase letters, numbers, and hyphens only.
                </p>
              )}
              {slugStatus === "taken" && (
                <p className="text-xs text-destructive">
                  This slug is already taken.
                </p>
              )}
              {slugStatus === "available" && (
                <p className="text-xs text-green-600">Slug is available.</p>
              )}
            </div>

            {/* Phone Number */}
            <div className="grid gap-2">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <PhoneInput
                defaultCountry="us"
                preferredCountries={["us"]}
                forceDialCode={true}
                value={phoneNumber}
                onChange={(phone, meta) => {
                  setCountryCode(meta.country.iso2);
                  setPhoneNumber(phone);
                }}
                className="[&_.react-international-phone-input-container]:rounded-md! [&_.react-international-phone-input]:bg-transparent! [&_.react-international-phone-input]:dark:bg-input/30! [&_.react-international-phone-input]:border-input! [&_.react-international-phone-input]:text-foreground! [&_.react-international-phone-input]:rounded-r-md! [&_.react-international-phone-input]:rounded-l-none!"
                inputClassName="h-9 w-full min-w-0 rounded-r-md rounded-l-none border border-input bg-transparent dark:bg-input/30 px-3 py-1 text-base md:text-sm shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 !bg-transparent dark:!bg-input/30 !border-input !rounded-r-md !rounded-l-none !text-foreground"
                countrySelectorStyleProps={{
                  buttonClassName:
                    "flex h-9 items-center justify-center !rounded-l-md border border-input !bg-transparent dark:!bg-input/30 px-3 hover:bg-accent hover:text-accent-foreground !border-input !text-foreground",
                  dropdownStyleProps: {
                    className:
                      "!bg-popover !text-popover-foreground rounded-md border !border-input shadow-md z-50 !p-1",
                    listItemClassName:
                      "!px-3 !py-2 hover:!bg-accent hover:!text-accent-foreground focus:!bg-accent focus:!text-accent-foreground focus-within:!bg-accent focus-within:!text-accent-foreground cursor-pointer !rounded-sm transition-colors !text-sm !text-foreground",
                    listItemSelectedClassName:
                      "!bg-accent !text-accent-foreground",
                  },
                }}
              />
            </div>

            {/* Email */}
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="e.g., contact@venue.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Organisation */}
            <div className="grid gap-2">
              <Label>
                Organisation <span className="text-destructive">*</span>
              </Label>
              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={popoverOpen}
                    className="justify-between"
                  >
                    {selectedOrganisation
                      ? selectedOrganisation.name
                      : "Select organisation..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command>
                    <CommandInput
                      placeholder="Search organisation..."
                      value={searchQuery}
                      onValueChange={setSearchQuery}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {isLoadingOrganisations
                          ? "Loading..."
                          : "No organisation found."}
                      </CommandEmpty>
                      <CommandGroup>
                        {organisationsData?.data?.map((organisation) => (
                          <CommandItem
                            key={organisation.id}
                            value={organisation.name}
                            onSelect={() => {
                              setSelectedOrganisationId(
                                selectedOrganisationId === organisation.id
                                  ? null
                                  : organisation.id
                              );
                              setPopoverOpen(false);
                              setSearchQuery("");
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedOrganisationId === organisation.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            {organisation.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Address Section */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium mb-3">
                Address <span className="text-destructive">*</span>
              </h3>

              {/* Address Line 1 */}
              <div className="grid gap-2 mb-3">
                <Label htmlFor="addressLine1">
                  Address Line 1 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="addressLine1"
                  placeholder="Street address"
                  value={address.addressLine1}
                  onChange={(e) =>
                    setAddress({ ...address, addressLine1: e.target.value })
                  }
                  required
                />
              </div>

              {/* Address Line 2 */}
              <div className="grid gap-2 mb-3">
                <Label htmlFor="addressLine2">Address Line 2</Label>
                <Input
                  id="addressLine2"
                  placeholder="Apartment, suite, etc."
                  value={address.addressLine2}
                  onChange={(e) =>
                    setAddress({ ...address, addressLine2: e.target.value })
                  }
                />
              </div>

              {/* City and State */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="grid gap-2">
                  <Label htmlFor="city">
                    City <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="city"
                    placeholder="City"
                    value={address.city}
                    onChange={(e) =>
                      setAddress({ ...address, city: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="state">
                    State <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="state"
                    placeholder="State"
                    value={address.state}
                    onChange={(e) =>
                      setAddress({ ...address, state: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              {/* Zipcode and Country */}
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="zipcode">
                    Zipcode <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="zipcode"
                    placeholder="10001"
                    value={address.zipcode}
                    onChange={(e) =>
                      setAddress({ ...address, zipcode: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="country">
                    Country <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="country"
                    placeholder="United States"
                    value={address.country}
                    onChange={(e) =>
                      setAddress({ ...address, country: e.target.value })
                    }
                    required
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t shrink-0 bg-background">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                mutation.isPending ||
                slugStatus === "checking" ||
                slugStatus === "taken" ||
                slugStatus === "invalid"
              }
            >
              {mutation.isPending ? "Updating..." : "Update Venue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
